// Plotly figures for the interference sky map: an az/el panorama, a 3D dome and a 2D polar (fisheye) view.
import { type ValueScale, formatMeasuredAt } from './rfiScan'
import { DEG, type Interpolator, type SkyPoint, normAz, toUnit } from './skyInterpolation'

export type DomeMode = 'sphere' | 'relief'

export type ValueGrid = Array<Array<number | null>>

export const COLORSCALE = 'Viridis'
const TELESCOPE_COLOR = '#d62728'
const GRID_LINE_COLOR = 'rgba(255,255,255,0.45)'
const HORIZON_COLOR = '#444'
const LABEL_COLOR = '#222'
const TRANSPARENT = 'rgba(0,0,0,0)'
const POLAR_RADIUS = 90
const POLAR_LABEL_OFFSET = 98
const POLAR_AXIS_RANGE = 104
const DOME_LABEL_RADIUS = 1.2
const DOME_POINT_OFFSET = 0.012
const RELIEF_HEIGHT = 0.35
const DOME_ELEVATION_RINGS = [30, 60]
const POLAR_ELEVATION_RINGS = [30, 60, 90]
const POLAR_SPOKE_STEP = 45
const PANORAMA_AZ_STEP = 1
const PANORAMA_EL_STEP = 0.25
// A wide band would otherwise mean hundreds of rows, each costing a row of interpolations, on a
// chart only a few hundred pixels high.
const PANORAMA_MAX_EL_ROWS = 120
// Elevation shown above and below the measured band.
const PANORAMA_EL_MARGIN = 1
const COMPASS_STEP = 45
const COMPASS_LABELS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'N']

export const formatHover = (az: number, el: number, v: number, scale: ValueScale) =>
  `az ${az.toFixed(0)}° · el ${el.toFixed(0)}°<br><b>${scale.format(v)}</b>`

const measuredLine = (p: SkyPoint) => (p.time ? `<br>measured ${formatMeasuredAt(p.time)}` : '<br>measured')

const colorbar = (scale: ValueScale, vmin: number, vmax: number, extra: object) => ({
  title: { text: scale.title },
  ...scale.ticks(vmin, vmax),
  ...extra,
})

// ---------------------------------------------------------------- sampling (expensive)

// Interpolated values on a 1° az/el grid; az 0..360 inclusive so the surface closes.
export function sampleDome(f: Interpolator): ValueGrid {
  const rows: ValueGrid = []
  for (let el = 0; el <= 90; el++) {
    const row: Array<number | null> = []
    for (let az = 0; az <= 360; az++) {
      row.push(f(az, el))
    }
    rows.push(row)
  }
  return rows
}

// Sky as seen from the ground: zenith in the centre, N up, E on the LEFT (sky chart convention).
export function toPolarXY(az: number, el: number): [number, number] {
  const r = POLAR_RADIUS - Math.max(el, 0)
  return [-r * Math.sin(az * DEG), r * Math.cos(az * DEG)]
}

export const polarAxis = Array.from({ length: 2 * POLAR_RADIUS + 1 }, (_, i) => i - POLAR_RADIUS)

// Interpolated values on a 1° pixel grid of the polar projection.
export function samplePolar(f: Interpolator): ValueGrid {
  return polarAxis.map((y) =>
    polarAxis.map((x) => {
      const r = Math.hypot(x, y)
      if (r > POLAR_RADIUS) return null
      return f(normAz(Math.atan2(-x, y) / DEG), POLAR_RADIUS - r)
    }),
  )
}

// ---------------------------------------------------------------- az/el panorama
//
// Measured scans cover a narrow band above the horizon; on the dome and the polar view it is a
// thin ring, so the panorama stretches the band over the full chart height.

export type Panorama = {
  az: number[]
  el: number[]
  values: ValueGrid
}

export function panoramaElevationRange([lo, hi]: [number, number]): [number, number] {
  return [lo - PANORAMA_EL_MARGIN, hi + PANORAMA_EL_MARGIN]
}

const steps = (from: number, to: number, step: number) =>
  Array.from({ length: Math.round((to - from) / step) + 1 }, (_, i) => from + i * step)

export function samplePanorama(f: Interpolator, band: [number, number]): Panorama {
  const [lo, hi] = panoramaElevationRange(band)
  const az = steps(0, 360, PANORAMA_AZ_STEP)
  const el = steps(lo, hi, Math.max(PANORAMA_EL_STEP, (hi - lo) / PANORAMA_MAX_EL_ROWS))
  const values = el.map((e) => az.map((a) => f(a, e)))
  return { az, el, values }
}

const formatPanoramaHover = (az: number, el: number, v: number, scale: ValueScale) =>
  `az ${az.toFixed(1)}° · el ${el.toFixed(2)}°<br><b>${scale.format(v)}</b>`

type PanoramaOptions = {
  panorama: Panorama
  points: SkyPoint[]
  showPoints: boolean
  vmin: number
  vmax: number
  scale: ValueScale
}

export function buildPanoramaTraces({ panorama, points, showPoints, vmin, vmax, scale }: PanoramaOptions): any[] {
  const text = panorama.values.map((row, i) =>
    row.map((v, j) => (v === null ? '' : formatPanoramaHover(panorama.az[j] % 360, panorama.el[i], v, scale))),
  )

  const traces: any[] = [
    {
      type: 'heatmap',
      x: panorama.az,
      y: panorama.el,
      z: panorama.values,
      text,
      hoverinfo: 'text',
      zmin: vmin,
      zmax: vmax,
      colorscale: COLORSCALE,
      colorbar: colorbar(scale, vmin, vmax, { thickness: 14 }),
    },
  ]

  if (showPoints) {
    traces.push({
      // Tens of thousands of points: WebGL, not one SVG node per marker.
      type: 'scattergl',
      mode: 'markers',
      x: points.map((p) => p.az),
      y: points.map((p) => p.el),
      // Scans are ~1° apart in azimuth: bigger or darker markers merge into solid lines.
      marker: { size: 2, color: 'rgba(0,0,0,0.25)' },
      text: points.map((p) => `${formatPanoramaHover(p.az, p.el, p.value, scale)}${measuredLine(p)}`),
      hoverinfo: 'text',
      showlegend: false,
    })
  }

  return traces
}

export function panoramaLayout(band: [number, number]) {
  return {
    uirevision: 'sky-panorama',
    autosize: true,
    margin: { l: 50, r: 10, t: 10, b: 40 },
    paper_bgcolor: TRANSPARENT,
    plot_bgcolor: TRANSPARENT,
    showlegend: false,
    xaxis: {
      range: [0, 360],
      tickvals: steps(0, 360, COMPASS_STEP),
      ticktext: COMPASS_LABELS,
      title: { text: 'Azimuth' },
    },
    yaxis: { range: panoramaElevationRange(band), title: { text: 'Elevation [°]' }, ticksuffix: '°' },
  }
}

export function isOnPanorama(el: number, band: [number, number]) {
  const [lo, hi] = panoramaElevationRange(band)
  return el >= lo && el <= hi
}

// ---------------------------------------------------------------- sample cache

export type SkySamples = {
  panorama: () => Panorama
  polar: () => ValueGrid
  dome: () => ValueGrid
}

export function once<T>(compute: () => T): () => T {
  let value: T | undefined
  return () => {
    if (value === undefined) value = compute()
    return value
  }
}

// Each view's grid is sampled the first time the view is shown and then kept, so switching back to
// a view does not interpolate it again.
export function lazySamples(f: Interpolator, band: [number, number]): SkySamples {
  return {
    panorama: once(() => samplePanorama(f, band)),
    polar: once(() => samplePolar(f)),
    dome: once(() => sampleDome(f)),
  }
}

// ---------------------------------------------------------------- 3D dome

export function domeRadius(v: number | null, mode: DomeMode, vmin: number, vmax: number) {
  if (mode !== 'relief' || v === null) return 1
  return 1 + RELIEF_HEIGHT * ((v - vmin) / (vmax - vmin || 1))
}

function ring3d(el: number, r: number, color: string, width: number) {
  const xs: number[] = []
  const ys: number[] = []
  const zs: number[] = []
  for (let az = 0; az <= 360; az += 2) {
    const u = toUnit(az, el)
    xs.push(r * u[0])
    ys.push(r * u[1])
    zs.push(r * u[2])
  }
  return { type: 'scatter3d', mode: 'lines', x: xs, y: ys, z: zs, line: { color, width }, hoverinfo: 'skip', showlegend: false }
}

type DomeOptions = {
  values: ValueGrid
  points: SkyPoint[]
  showPoints: boolean
  mode: DomeMode
  vmin: number
  vmax: number
  scale: ValueScale
}

export function buildDomeTraces({ values, points, showPoints, mode, vmin, vmax, scale }: DomeOptions): any[] {
  const X: Array<Array<number | null>> = []
  const Y: Array<Array<number | null>> = []
  const Z: Array<Array<number | null>> = []
  const T: string[][] = []

  values.forEach((row, el) => {
    const rx: Array<number | null> = []
    const ry: Array<number | null> = []
    const rz: Array<number | null> = []
    const rt: string[] = []
    row.forEach((v, az) => {
      if (v === null) {
        rx.push(null)
        ry.push(null)
        rz.push(null)
        rt.push('')
        return
      }
      const r = domeRadius(v, mode, vmin, vmax)
      const u = toUnit(az, el)
      rx.push(r * u[0])
      ry.push(r * u[1])
      rz.push(r * u[2])
      rt.push(formatHover(az % 360, el, v, scale))
    })
    X.push(rx)
    Y.push(ry)
    Z.push(rz)
    T.push(rt)
  })

  const traces: any[] = [
    {
      type: 'surface',
      x: X,
      y: Y,
      z: Z,
      surfacecolor: values,
      cmin: vmin,
      cmax: vmax,
      colorscale: COLORSCALE,
      colorbar: colorbar(scale, vmin, vmax, { len: 0.7, thickness: 14 }),
      text: T,
      hoverinfo: 'text',
      lighting: { ambient: 0.85, diffuse: 0.35, specular: 0.05, roughness: 0.9 },
    },
    ring3d(0, 1.02, HORIZON_COLOR, 4),
  ]

  if (mode === 'sphere') {
    DOME_ELEVATION_RINGS.forEach((el) => traces.push(ring3d(el, 1.004, GRID_LINE_COLOR, 2)))
  }

  // x = east, y = north
  traces.push({
    type: 'scatter3d',
    mode: 'text',
    x: [0, DOME_LABEL_RADIUS, 0, -DOME_LABEL_RADIUS],
    y: [DOME_LABEL_RADIUS, 0, -DOME_LABEL_RADIUS, 0],
    z: [0, 0, 0, 0],
    text: ['N', 'E', 'S', 'W'],
    textfont: { size: 16, color: LABEL_COLOR },
    hoverinfo: 'skip',
    showlegend: false,
  })

  if (showPoints) {
    const xyz = points.map((p) => {
      const r = domeRadius(p.value, mode, vmin, vmax) + DOME_POINT_OFFSET
      return toUnit(p.az, p.el).map((c) => r * c)
    })
    traces.push({
      type: 'scatter3d',
      mode: 'markers',
      x: xyz.map((c) => c[0]),
      y: xyz.map((c) => c[1]),
      z: xyz.map((c) => c[2]),
      marker: { size: 2, color: '#111' },
      text: points.map((p) => `${formatHover(p.az, p.el, p.value, scale)}${measuredLine(p)}`),
      hoverinfo: 'text',
      showlegend: false,
    })
  }

  return traces
}

export const domeLayout = {
  uirevision: 'sky-dome',
  autosize: true,
  margin: { l: 0, r: 0, t: 0, b: 0 },
  paper_bgcolor: TRANSPARENT,
  scene: {
    aspectmode: 'data',
    xaxis: { visible: false },
    yaxis: { visible: false },
    zaxis: { visible: false },
    camera: { eye: { x: 0.85, y: -1.15, z: 0.75 } },
  },
}

// ---------------------------------------------------------------- 2D polar

type PolarOptions = {
  values: ValueGrid
  points: SkyPoint[]
  showPoints: boolean
  vmin: number
  vmax: number
  scale: ValueScale
}

export function buildPolarTraces({ values, points, showPoints, vmin, vmax, scale }: PolarOptions): any[] {
  const text = values.map((row, i) =>
    row.map((v, j) => {
      if (v === null) return ''
      const x = polarAxis[j]
      const y = polarAxis[i]
      return formatHover(normAz(Math.atan2(-x, y) / DEG), POLAR_RADIUS - Math.hypot(x, y), v, scale)
    }),
  )

  const traces: any[] = [
    {
      type: 'heatmap',
      x: polarAxis,
      y: polarAxis,
      z: values,
      text,
      hoverinfo: 'text',
      zmin: vmin,
      zmax: vmax,
      colorscale: COLORSCALE,
      colorbar: colorbar(scale, vmin, vmax, { len: 0.7, thickness: 14 }),
    },
  ]

  if (showPoints) {
    const xy = points.map((p) => toPolarXY(p.az, p.el))
    traces.push({
      // Tens of thousands of points: WebGL, not one SVG node per marker.
      type: 'scattergl',
      mode: 'markers',
      x: xy.map((c) => c[0]),
      y: xy.map((c) => c[1]),
      marker: { size: 3, color: 'rgba(0,0,0,0.55)' },
      text: points.map((p) => formatHover(p.az, p.el, p.value, scale)),
      hoverinfo: 'text',
      showlegend: false,
    })
  }

  return traces
}

function polarShapes() {
  const shapes: any[] = POLAR_ELEVATION_RINGS.map((r) => {
    const isHorizon = r === POLAR_RADIUS
    return {
      type: 'circle',
      x0: -r,
      y0: -r,
      x1: r,
      y1: r,
      line: { color: isHorizon ? HORIZON_COLOR : GRID_LINE_COLOR, width: isHorizon ? 2 : 1, dash: isHorizon ? 'solid' : 'dot' },
    }
  })
  for (let az = 0; az < 360; az += POLAR_SPOKE_STEP) {
    const [x, y] = toPolarXY(az, 0)
    shapes.push({ type: 'line', x0: 0, y0: 0, x1: x, y1: y, line: { color: GRID_LINE_COLOR, width: 1, dash: 'dot' } })
  }
  return shapes
}

const label = (text: string, x: number, y: number, extra: object = {}) => ({
  text,
  x,
  y,
  showarrow: false,
  font: { size: 15, color: LABEL_COLOR },
  ...extra,
})

const elevationLabel = { font: { size: 10, color: '#fff' }, xanchor: 'left' }

export const polarLayout = {
  uirevision: 'sky-polar',
  autosize: true,
  margin: { l: 10, r: 10, t: 10, b: 10 },
  paper_bgcolor: TRANSPARENT,
  plot_bgcolor: TRANSPARENT,
  showlegend: false,
  xaxis: { visible: false, range: [-POLAR_AXIS_RANGE, POLAR_AXIS_RANGE], fixedrange: true },
  yaxis: { visible: false, range: [-POLAR_AXIS_RANGE, POLAR_AXIS_RANGE], scaleanchor: 'x', fixedrange: true },
  shapes: polarShapes(),
  annotations: [
    label('N', 0, POLAR_LABEL_OFFSET),
    label('S', 0, -POLAR_LABEL_OFFSET),
    label('E', -POLAR_LABEL_OFFSET, 0),
    label('W', POLAR_LABEL_OFFSET, 0),
    label('60°', 2, 30, elevationLabel),
    label('30°', 2, 60, elevationLabel),
  ],
}

// ---------------------------------------------------------------- telescope pointing
//
// On the ground view a dashed line runs from the centre to a crosshair where the beam meets the sky.

export type Pointing = {
  az: number
  el: number
  hover: string
}

const BEAM_LINE = { color: TELESCOPE_COLOR, width: 4, dash: 'dash' }

// Open ring with a cross inside, drawn as two markers at the same spot.
function crosshair2d(x: number, y: number, hover: string, ringSize: number): any[] {
  return [
    {
      type: 'scatter',
      mode: 'markers',
      x: [x],
      y: [y],
      marker: { size: ringSize, color: TRANSPARENT, line: { color: TELESCOPE_COLOR, width: 3 }, symbol: 'circle' },
      hovertext: [hover],
      hoverinfo: 'text',
      showlegend: false,
    },
    {
      type: 'scatter',
      mode: 'markers',
      x: [x],
      y: [y],
      marker: { size: ringSize * 1.6, color: TELESCOPE_COLOR, symbol: 'cross-thin', line: { color: TELESCOPE_COLOR, width: 2 } },
      hoverinfo: 'skip',
      showlegend: false,
    },
  ]
}

export function buildPolarTelescopeTraces(pointing: Pointing): any[] {
  const [x, y] = toPolarXY(pointing.az, pointing.el)
  return [
    {
      type: 'scatter',
      mode: 'lines',
      x: [0, x],
      y: [0, y],
      line: BEAM_LINE,
      hoverinfo: 'skip',
      showlegend: false,
    },
    ...crosshair2d(x, y, pointing.hover, 18),
  ]
}

export function buildPanoramaTelescopeTraces(pointing: Pointing): any[] {
  return crosshair2d(pointing.az, pointing.el, pointing.hover, 14)
}
